import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { 
  Clock, 
  Shield, 
  Smartphone, 
  TrendingDown,
  Zap,
  Heart,
  Star
} from 'lucide-react';
import MyButton from '../../../components/common/Button/MyButton';
import '../../../assets/styles/customer/layout/WhyChooseSection.css';

const WhyChooseSection = () => {
  const benefits = [
    {
      id: 1,
      icon: <Clock size={32} strokeWidth={2} />,
      stat: '2+ hrs',
      label: 'saved daily',
      title: 'Save Time Daily',
      description: 'No more manual diary entries. Digital records save distributors 2+ hours every day.',
      iconBg: 'rgba(62, 150, 231, 0.1)',
      iconColor: '#3e96e7'
    },
    {
      id: 2,
      icon: <Shield size={32} strokeWidth={2} />,
      stat: '100%',
      label: 'transparent',
      title: 'Build Trust',
      description: 'Transparent records that both customers and distributors can verify anytime.',
      iconBg: 'rgba(62, 150, 231, 0.1)',
      iconColor: '#3e96e7'
    },
    {
      id: 3,
      icon: <Smartphone size={32} strokeWidth={2} />,
      stat: 'Any',
      label: 'device',
      title: 'Works Everywhere',
      description: 'Mobile-first design works on any smartphone. No expensive devices needed.',
      iconBg: 'rgba(62, 150, 231, 0.1)',
      iconColor: '#3e96e7'
    },
    {
      id: 4,
      icon: <TrendingDown size={32} strokeWidth={2} />,
      stat: '90%',
      label: 'less disputes',
      title: 'Reduce Disputes',
      description: 'Clear daily logs eliminate billing disagreements and payment confusion.',
      iconBg: 'rgba(62, 150, 231, 0.1)',
      iconColor: '#3e96e7'
    },
    {
      id: 5,
      icon: <Zap size={32} strokeWidth={2} />,
      stat: 'Real',
      label: 'time sync',
      title: 'Instant Updates',
      description: 'Real-time notifications keep everyone informed about deliveries and payments.',
      iconBg: 'rgba(62, 150, 231, 0.1)',
      iconColor: '#3e96e7'
    },
    {
      id: 6,
      icon: <Heart size={32} strokeWidth={2} />,
      stat: 'Win',
      label: 'win model',
      title: 'Grow Together',
      description: 'Help distributors expand their business while customers get reliable service.',
      iconBg: 'rgba(62, 150, 231, 0.1)',
      iconColor: '#3e96e7'
    }
  ];

  return (
    <section className="why-choose-section">
      
      {/* Floating Milk Bottles */}
      <div className="why-choose-floating-milk">
        <div className="floating-bottle bottle-1">🥛</div>
        <div className="floating-bottle bottle-2">🥛</div>
        <div className="floating-bottle bottle-3">🥛</div>
        <div className="floating-bottle bottle-4">🥛</div>
      </div>

      {/* Wavy Background */}
      <div className="why-choose-waves">
        <svg className="wave-bg wave-1" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="rgba(62, 150, 231, 0.03)" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,165.3C1248,171,1344,149,1392,138.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
        <svg className="wave-bg wave-2" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="rgba(76, 175, 80, 0.02)" d="M0,224L48,213.3C96,203,192,181,288,176C384,171,480,181,576,192C672,203,768,213,864,208C960,203,1056,181,1152,170.7C1248,160,1344,160,1392,160L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>

      <Container>
        
        {/* Section Header */}
        <Row>
          <Col lg={5} md={12}>
            <div className="why-choose-header">
              <div className="why-badge">Why MilkFlow?</div>
              <h2 className="why-title">
                The Smarter Way to<br />
                <span className="why-title-highlight">Manage Milk Delivery</span>
              </h2>
              <p className="why-description">
                MilkFlow replaces outdated paper diaries with a modern digital solution 
                that benefits everyone in the dairy supply chain. From doorstep to 
                dashboard, we've got you covered.
              </p>

              {/* CTA Buttons */}
              <div className="why-cta-buttons">
                <MyButton variant="primary" className="why-start-btn">
                  Start Free Today
                </MyButton>
                <button className="why-learn-btn">
                  Learn More About Us
                </button>
              </div>

              {/* Social Proof */}
              <div className="social-proof">
                <div className="proof-avatars">
                  <div className="avatar avatar-1">A</div>
                  <div className="avatar avatar-2">B</div>
                  <div className="avatar avatar-3">C</div>
                  <div className="avatar avatar-4">D</div>
                </div>
                <div className="proof-text">
                  <p className="proof-count">500+ Distributors</p>
                  <div className="proof-rating">
                    <Star size={16} fill="#ffc107" color="#ffc107" />
                    <Star size={16} fill="#ffc107" color="#ffc107" />
                    <Star size={16} fill="#ffc107" color="#ffc107" />
                    <Star size={16} fill="#ffc107" color="#ffc107" />
                    <Star size={16} fill="#ffc107" color="#ffc107" />
                    <span className="rating-text">4.9/5 Rating</span>
                  </div>
                </div>
              </div>
            </div>
          </Col>

          {/* Benefits Grid */}
          <Col lg={7} md={12}>
            <Row className="g-4 benefits-grid">
              {benefits.map((benefit, index) => (
                <Col lg={6} md={6} sm={6} xs={12} key={benefit.id}>
                  <div 
                    className="benefit-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Stat Badge */}
                    <div className="benefit-stat">
                      <span className="stat-number">{benefit.stat}</span>
                      <span className="stat-label">{benefit.label}</span>
                    </div>

                    {/* Icon */}
                    <div 
                      className="benefit-icon-box"
                      style={{ 
                        backgroundColor: benefit.iconBg,
                        color: benefit.iconColor
                      }}
                    >
                      {benefit.icon}
                    </div>

                    {/* Content */}
                    <h3 className="benefit-title">{benefit.title}</h3>
                    <p className="benefit-description">{benefit.description}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>

      </Container>
    </section>
  );
};

export default WhyChooseSection;